/* eslint-disable import/no-unresolved */
import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import useScreen from 'hooks/useScreen';
import CustomCarouselArrows from './CustomCarouselArrows';

const BEFORE_SLIDE_FN_DEFAULT = () => {};
const AFTER_SLIDE_FN_DEFAULT = () => {};

const Carousel = loadable(() => import('./WrappedCarousel'));

// TODO: Investigate why forwardRef is needed here
const ForwardRefCarousel = React.forwardRef((props, ref) => (
  <Carousel carouselRef={ref} {...props} />
));

const NewSliderComponent = ({
  children,
  deviceOverride = false,
  disableSliding,
  fabricGridControls = false,
  finalVisibleSlides,
  isMobileMaterialSwatches = false,
  navType,
  variant,
  beforeSlideFn = BEFORE_SLIDE_FN_DEFAULT,
  afterSlideFn = AFTER_SLIDE_FN_DEFAULT,
}) => {
  const carouselRef = useRef(null);

  const { isMobile } = useScreen();

  const showLeftAndRightPartially =
    finalVisibleSlides < 2 && finalVisibleSlides > 1;

  const finalSlides = children;

  // Calculate visible slides based on screen size and overrides
  const visibleSlides = useMemo(() => {
    if (deviceOverride) {
      return finalVisibleSlides;
    }

    if (finalVisibleSlides) {
      return finalVisibleSlides;
    }

    const defaultSlides = isMobile ? 2 : 4;

    return Math.min(finalSlides.length, defaultSlides);
  }, [deviceOverride, finalVisibleSlides, isMobile, finalSlides.length]);

  if (!finalSlides.length) {
    return (
      <div
        className={classNames(
          'mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover components [&_.slider-control-bottomcenter]:w-full',
          {
            '!mt-0': variant === 'material-swatches-sidebar',
            '!min-h-[250px] max-[400px]:!min-h-[400px]':
              variant === 'blog-slider',
          }
        )}
      />
    );
  }

  const hideNav = visibleSlides >= finalSlides.length || navType === '';

  // TODO: Refactor JSX to be leaner

  return (
    <div
      className={classNames(
        'NewSliderComponent components [&_.slider-control-bottomcenter]:w-full',
        {
          '[&_.slider-control-centerleft]:items-stretch [&_.slider-control-centerleft]:!bottom-0 [&_.slider-control-centerleft]:flex [&_.slider-control-centerleft]:justify-stretch [&_.slider-control-centerleft]:absolute [&_.slider-control-centerleft]:!top-0 [&_.slider-control-centerleft]:![transform:none] [&_.slider-control-centerleft]:!w-[calc(9%_-_9px)] [&_.slider-control-centerright]:items-stretch [&_.slider-control-centerright]:!bottom-0 [&_.slider-control-centerright]:flex [&_.slider-control-centerright]:justify-stretch [&_.slider-control-centerright]:absolute [&_.slider-control-centerright]:!top-0 [&_.slider-control-centerright]:![transform:none] [&_.slider-control-centerright]:!w-[calc(9%_-_9px)]': fabricGridControls,
          'mt-5 [&_.slider]:min-h-[1px] [&_.slider]:m-auto [&_.slider-slide>img]:object-cover [&_.slider-control-centerleft]:!left-0 [&_.slider-control-centerright]:!right-0': !isMobileMaterialSwatches,
          '!mt-0': variant === 'material-swatches-sidebar',
          'pt-1 !mt-0': variant === 'blog-slider',
        }
      )}
    >
      {variant === 'blog-slider' && !isMobile && !hideNav && (
        <div className="w-full flex justify-end gap-2 mb-2 mr-1">
          <button
            type="button"
            className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray"
            aria-label="Previous slide"
            onClick={() => {
              carouselRef.current?.goBack();
            }}
          >
            <IconArrowLeftWhite />
          </button>
          <button
            type="button"
            className="group/SliderButton w-[50px] h-[50px] flex justify-center items-center opacity-100 text-[0] leading-[0] cursor-pointer text-transparent border border-solid border-gray-light6 outline-0 transition-all hover:border-brand bg-transparent [&_svg_path]:fill-gray"
            aria-label="Next slide"
            onClick={() => {
              carouselRef.current?.goForward();
            }}
          >
            <IconArrowRightWhite />
          </button>
        </div>
      )}
      {isMobile && !isMobileMaterialSwatches ? (
        <div className="overflow-hidden">
          <div
            className={classNames(
              '-ml-5 -mr-5 flex overflow-scroll overflow-y-hidden',
              'max-[767px]:ml-0 max-[767px]:mr-0',
              '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
              'h-[425px]',
              {
                'overflow-scroll overflow-y-hidden flex':
                  variant === 'blog-slider' || variant === 'collections-slider',
              }
            )}
          >
            {finalSlides}
          </div>
        </div>
      ) : (
        <ForwardRefCarousel
          className="space-x-0 md:space-x-5"
          ref={carouselRef}
          showArrows={variant !== 'blog-slider'}
          arrows={<CustomCarouselArrows variant={variant} />}
          wrapAround={
            showLeftAndRightPartially
              ? true
              : finalVisibleSlides < finalSlides.length
          }
          dragging={!showLeftAndRightPartially}
          swiping={!disableSliding}
          beforeSlide={beforeSlideFn}
          afterSlide={afterSlideFn}
          scrollDistance="slide"
        >
          {finalSlides}
        </ForwardRefCarousel>
      )}
    </div>
  );
};

export default NewSliderComponent;
